(() => {
  // src/core/library.js
  function compareText(left, right) {
    if (left === right) return 0;
    return left < right ? -1 : 1;
  }
  var INITIAL_VISIBLE_CONNECTIONS = 24;
  var LOAD_MORE_INCREMENT = 24;
  function createConnectionBatch(connections, visibleLimit = INITIAL_VISIBLE_CONNECTIONS) {
    const sourceConnections = Array.isArray(connections) ? connections : [];
    const normalizedLimit = Number.isInteger(visibleLimit) && visibleLimit >= 0 ? visibleLimit : INITIAL_VISIBLE_CONNECTIONS;
    const visibleConnections = sourceConnections.slice(0, normalizedLimit);
    return {
      connections: visibleConnections,
      visibleCount: visibleConnections.length,
      totalCount: sourceConnections.length,
      hasMore: visibleConnections.length < sourceConnections.length
    };
  }
  function getNextVisibleConnectionLimit(currentLimit, totalCount, increment = LOAD_MORE_INCREMENT) {
    const normalizedCurrent = Number.isInteger(currentLimit) && currentLimit >= 0 ? currentLimit : INITIAL_VISIBLE_CONNECTIONS;
    const normalizedTotal = Number.isInteger(totalCount) && totalCount >= 0 ? totalCount : 0;
    const normalizedIncrement = Number.isInteger(increment) && increment > 0 ? increment : LOAD_MORE_INCREMENT;
    return Math.min(normalizedTotal, normalizedCurrent + normalizedIncrement);
  }
  function normalizePartId(value) {
    return String(value ?? "").trim().toLocaleLowerCase("en-US");
  }
  function parsePartIdInput(value) {
    return String(value ?? "").split(/[\s,]+/).map((partId) => partId.trim()).filter(Boolean);
  }
  function canonicalizePartIds(partIds, partsIndex) {
    const canonicalByNormalizedId = new Map(
      Object.keys(partsIndex ?? {}).map((partId) => [normalizePartId(partId), partId])
    );
    const selected = [];
    const seen = /* @__PURE__ */ new Set();
    for (const value of Array.isArray(partIds) ? partIds : [partIds]) {
      const trimmed = String(value ?? "").trim();
      const normalized = normalizePartId(trimmed);
      if (normalized === "" || seen.has(normalized)) continue;
      seen.add(normalized);
      selected.push(canonicalByNormalizedId.get(normalized) ?? trimmed);
    }
    return selected;
  }
  function shuffleConnections(connections, random = Math.random) {
    const shuffled = Array.isArray(connections) ? [...connections] : [];
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const randomValue = random();
      if (!Number.isFinite(randomValue) || randomValue < 0 || randomValue >= 1) {
        throw new RangeError("Random values must be finite numbers from 0 up to, but not including, 1.");
      }
      const target = Math.floor(randomValue * (index + 1));
      [shuffled[index], shuffled[target]] = [shuffled[target], shuffled[index]];
    }
    return shuffled;
  }
  function getConnectionPartIds(connection) {
    const partIds = [
      ...Array.isArray(connection?.baseParts) ? connection.baseParts : [],
      ...Array.isArray(connection?.connectionParts) ? connection.connectionParts : []
    ].map((part) => part.partId);
    return [...new Set(partIds)].sort(compareText);
  }
  function getPartMetadata(partId, partsMetadata) {
    if (!partsMetadata || typeof partsMetadata !== "object" || Array.isArray(partsMetadata)) {
      return void 0;
    }
    const normalizedPartId = normalizePartId(partId);
    const metadataKey = Object.keys(partsMetadata).find(
      (candidate) => normalizePartId(candidate) === normalizedPartId
    );
    return metadataKey === void 0 ? void 0 : partsMetadata[metadataKey];
  }
  function normalizePartSearchQuery(value) {
    return String(value ?? "").normalize("NFKD").toLocaleLowerCase("en-US").replace(/[^\p{L}\p{N}]+/gu, " ").trim().replace(/\s+/g, " ");
  }
  function findMatchingParts(query, partsMetadata, { limit = Infinity } = {}) {
    const normalizedQuery = normalizePartSearchQuery(query);
    if (normalizedQuery === "" || !partsMetadata || typeof partsMetadata !== "object") return [];
    const queryTokens = normalizedQuery.split(" ");
    const matches = [];
    for (const [partId, metadata] of Object.entries(partsMetadata)) {
      if (!metadata || typeof metadata.name !== "string") continue;
      const normalizedPartId = normalizePartSearchQuery(partId);
      const normalizedName = normalizePartSearchQuery(metadata.name);
      const searchable = `${normalizedPartId} ${normalizedName}`;
      if (!queryTokens.every((token) => searchable.includes(token))) continue;
      const rank = normalizedPartId === normalizedQuery ? 0 : normalizedPartId.startsWith(normalizedQuery) ? 1 : normalizedName === normalizedQuery ? 2 : normalizedName.startsWith(normalizedQuery) ? 3 : 4;
      matches.push({ partId, name: metadata.name, rank });
    }
    return matches.sort(
      (left, right) => left.rank - right.rank || left.name.length - right.name.length || compareText(left.partId, right.partId)
    ).slice(0, Math.max(0, limit));
  }
  function findExactPartMatch(query, partsMetadata) {
    const normalizedQuery = normalizePartSearchQuery(query);
    if (normalizedQuery === "") return void 0;
    const matches = findMatchingParts(query, partsMetadata);
    const exactIdMatch = matches.find(
      ({ partId }) => normalizePartSearchQuery(partId) === normalizedQuery
    );
    if (exactIdMatch) return exactIdMatch;
    const exactNameMatches = matches.filter(
      ({ name }) => normalizePartSearchQuery(name) === normalizedQuery
    );
    return exactNameMatches.length === 1 ? exactNameMatches[0] : void 0;
  }
  function validatePartsMetadata(partsIndex, partsMetadata) {
    const errors = [];
    if (!partsMetadata || typeof partsMetadata !== "object" || Array.isArray(partsMetadata)) {
      return {
        valid: false,
        errors: ["Part metadata must be an object."],
        missingPartIds: Object.keys(partsIndex ?? {}).sort(compareText)
      };
    }
    for (const [partId, metadata] of Object.entries(partsMetadata)) {
      if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
        errors.push(`Part metadata entry ${partId} must be an object.`);
        continue;
      }
      if (typeof metadata.name !== "string" || metadata.name.trim() === "") {
        errors.push(`Part metadata entry ${partId} is missing a valid name.`);
      }
      if (typeof metadata.source !== "string" || metadata.source.trim() === "") {
        errors.push(`Part metadata entry ${partId} is missing a valid source.`);
      }
    }
    const missingPartIds = Object.keys(partsIndex ?? {}).filter((partId) => getPartMetadata(partId, partsMetadata) === void 0).sort(compareText);
    return { valid: errors.length === 0, errors, missingPartIds };
  }
  function getConnectionPartIdsInDisplayOrder(connection) {
    const parts = [
      ...Array.isArray(connection?.baseParts) ? connection.baseParts : [],
      ...Array.isArray(connection?.connectionParts) ? connection.connectionParts : []
    ];
    const seen = /* @__PURE__ */ new Set();
    return parts.map((part) => String(part?.partId ?? "").trim()).filter((partId) => {
      const normalizedPartId = normalizePartId(partId);
      if (normalizedPartId === "" || seen.has(normalizedPartId)) return false;
      seen.add(normalizedPartId);
      return true;
    });
  }
  function joinNaturalLanguage(items) {
    if (items.length <= 1) return items[0] ?? "";
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(", ")}, and ${items.at(-1)}`;
  }
  function createConnectionAltText(connection, partsMetadata) {
    const partDescriptions = getConnectionPartIdsInDisplayOrder(connection).map((partId) => {
      const name = getPartMetadata(partId, partsMetadata)?.name;
      return typeof name === "string" && name.trim() !== "" ? `${partId} ${name}` : partId;
    });
    if (partDescriptions.length === 0) return "Creative parts connection.";
    return `Creative parts connection using ${joinNaturalLanguage(partDescriptions)}.`;
  }
  function validateLibraryConsistency(connections, partsIndex) {
    const errors = [];
    if (!Array.isArray(connections)) {
      return { valid: false, errors: ["Connection data must be an array."] };
    }
    if (!partsIndex || typeof partsIndex !== "object" || Array.isArray(partsIndex)) {
      return { valid: false, errors: ["Parts index must be an object."] };
    }
    const connectionsById = /* @__PURE__ */ new Map();
    const expectedIndex = /* @__PURE__ */ new Map();
    for (const connection of connections) {
      if (!connection || typeof connection.id !== "string") {
        errors.push("A connection is missing a valid ID.");
        continue;
      }
      if (connectionsById.has(connection.id)) {
        errors.push(`Duplicate connection ID ${connection.id}.`);
        continue;
      }
      if (!Array.isArray(connection.baseParts) || !Array.isArray(connection.connectionParts)) {
        errors.push(`${connection.id} is missing a valid parts role array.`);
        continue;
      }
      connectionsById.set(connection.id, connection);
      for (const partId of getConnectionPartIds(connection)) {
        const ids = expectedIndex.get(partId) ?? /* @__PURE__ */ new Set();
        ids.add(connection.id);
        expectedIndex.set(partId, ids);
      }
    }
    for (const [partId, connectionIds] of Object.entries(partsIndex)) {
      if (!Array.isArray(connectionIds)) {
        errors.push(`Index entry ${partId} must contain an array.`);
        continue;
      }
      if (new Set(connectionIds).size !== connectionIds.length) {
        errors.push(`Index entry ${partId} contains duplicate connection IDs.`);
      }
      for (const connectionId of connectionIds) {
        if (!connectionsById.has(connectionId)) {
          errors.push(`Index entry ${partId} references missing ${connectionId}.`);
        }
      }
      const expected = [...expectedIndex.get(partId) ?? []].sort(compareText);
      const actual = [...new Set(connectionIds)].sort(compareText);
      if (JSON.stringify(actual) !== JSON.stringify(expected)) {
        errors.push(`Index entry ${partId} does not match connection data.`);
      }
    }
    for (const partId of expectedIndex.keys()) {
      if (!Object.hasOwn(partsIndex, partId)) {
        errors.push(`Part ID ${partId} is missing from the index.`);
      }
    }
    return { valid: errors.length === 0, errors };
  }
  function filterConnectionsByPartIds(connections, partIds, partsIndex) {
    const sourceConnections = Array.isArray(connections) ? connections : [];
    const selectedPartIds = canonicalizePartIds(partIds, partsIndex);
    if (selectedPartIds.length === 0) return [...sourceConnections];
    const entriesByNormalizedId = new Map(
      Object.entries(partsIndex ?? {}).map(([partId, connectionIds]) => [
        normalizePartId(partId),
        Array.isArray(connectionIds) ? connectionIds : []
      ])
    );
    const matchingSets = selectedPartIds.map(
      (partId) => new Set(entriesByNormalizedId.get(normalizePartId(partId)) ?? [])
    );
    return sourceConnections.filter(
      (connection) => matchingSets.every((connectionIds) => connectionIds.has(connection.id))
    );
  }
  function createFilterState(connections, partsIndex, partIds) {
    const selectedPartIds = canonicalizePartIds(partIds, partsIndex);
    const knownPartIds = new Set(Object.keys(partsIndex ?? {}).map(normalizePartId));
    return {
      selectedPartIds,
      unknownPartIds: selectedPartIds.filter(
        (partId) => !knownPartIds.has(normalizePartId(partId))
      ),
      connections: filterConnectionsByPartIds(connections, selectedPartIds, partsIndex)
    };
  }
  function filterConnectionsBySearchState(connections, { exactPartIds = [], keywordQuery = "" }, partsIndex, partsMetadata) {
    const exactState = createFilterState(connections, partsIndex, exactPartIds);
    const normalizedKeywordQuery = normalizePartSearchQuery(keywordQuery);
    const matchingParts = findMatchingParts(keywordQuery, partsMetadata);
    if (normalizedKeywordQuery === "") {
      return { ...exactState, keywordQuery: "", matchingParts: [] };
    }
    const matchingPartIds = new Set(matchingParts.map(({ partId }) => normalizePartId(partId)));
    const matchingConnections = exactState.connections.filter(
      (connection) => getConnectionPartIds(connection).some((partId) => matchingPartIds.has(normalizePartId(partId)))
    );
    return {
      ...exactState,
      keywordQuery: normalizedKeywordQuery,
      matchingParts,
      connections: matchingConnections
    };
  }

  // src/core/sharing.js
  function buildConnectionDeepLink(pageUrl, connectionId) {
    const url = new URL(pageUrl);
    url.search = "";
    url.hash = "";
    url.searchParams.set("connection", String(connectionId));
    return url.href;
  }
  function removeConnectionDeepLink(pageUrl) {
    const url = new URL(pageUrl);
    url.searchParams.delete("connection");
    return url.href;
  }
  function resolveDeepLinkedConnection(pageUrl, connections) {
    const connectionId = new URL(pageUrl).searchParams.get("connection");
    if (connectionId === null || !Array.isArray(connections)) return void 0;
    return connections.find((connection) => connection?.id === connectionId);
  }
  function createConnectionShareText(connection, partsMetadata) {
    return createConnectionAltText(connection, partsMetadata);
  }
  function buildConnectionShareTargets({
    pageUrl,
    connection,
    partsMetadata,
    imageBaseUrl
  }) {
    const deepLinkUrl = buildConnectionDeepLink(pageUrl, connection.id);
    const imageUrl = new URL(connection.image, imageBaseUrl).href;
    const text = createConnectionShareText(connection, partsMetadata);
    const textWithUrl = `${text} ${deepLinkUrl}`;
    const facebook = new URL("https://www.facebook.com/sharer/sharer.php");
    facebook.searchParams.set("u", deepLinkUrl);
    const pinterest = new URL("https://www.pinterest.com/pin/create/button/");
    pinterest.searchParams.set("url", deepLinkUrl);
    pinterest.searchParams.set("media", imageUrl);
    pinterest.searchParams.set("description", text);
    const whatsapp = new URL("https://wa.me/");
    whatsapp.searchParams.set("text", textWithUrl);
    const x = new URL("https://twitter.com/intent/tweet");
    x.searchParams.set("text", text);
    x.searchParams.set("url", deepLinkUrl);
    return {
      deepLinkUrl,
      imageUrl,
      text,
      facebookUrl: facebook.href,
      pinterestUrl: pinterest.href,
      whatsappUrl: whatsapp.href,
      xUrl: x.href
    };
  }
  function supportsNativeShare(navigatorLike) {
    return typeof navigatorLike?.share === "function";
  }
  async function copyTextWithFallback(text, { clipboard, fallback } = {}) {
    if (typeof clipboard?.writeText === "function") {
      try {
        await clipboard.writeText(text);
        return "clipboard";
      } catch {
      }
    }
    if (typeof fallback === "function" && await fallback(text)) return "fallback";
    throw new Error("Copy is unavailable.");
  }

  // src/ui/library-ui.js
  function createElement(name, className, text) {
    const element = document.createElement(name);
    if (className) element.className = className;
    if (text !== void 0) element.textContent = text;
    return element;
  }
  function renderParts(parts, role, onPartSelected, partsMetadata) {
    const section = createElement("section", `htbi-connection-parts htbi-connection-parts--${role}`);
    const label = role === "base" ? "Base Parts" : "Connection Parts";
    section.append(createElement("h3", "htbi-connection-parts-title", label));
    const list = createElement("ul", "htbi-connection-parts-list");
    for (const part of parts) {
      const item = createElement("li", "htbi-connection-part-row");
      const metadata = getPartMetadata(part.partId, partsMetadata);
      const button = createElement("button", "htbi-connection-part-button");
      button.type = "button";
      const identity = createElement("span", "htbi-connection-part-identity");
      identity.append(createElement("span", "htbi-connection-part-id", part.partId));
      if (metadata?.name) {
        identity.append(createElement("span", "htbi-connection-part-name", metadata.name));
      }
      button.append(identity);
      const accessibleName = metadata?.name ? `, ${metadata.name}` : "";
      button.setAttribute("aria-label", `Add Part ID ${part.partId}${accessibleName} to filters`);
      button.addEventListener("click", () => onPartSelected(part.partId));
      const quantity = createElement("span", "htbi-connection-quantity", `\xD7 ${part.quantity}`);
      quantity.setAttribute("aria-label", `quantity ${part.quantity}`);
      item.append(button, quantity);
      list.append(item);
    }
    section.append(list);
    return section;
  }
  function renderImage(connection, imageBaseUrl, partsMetadata, { modal = false, onOpen } = {}) {
    const wrapper = createElement("div", modal ? "htbi-connection-modal-image" : "htbi-connection-image-frame");
    const image = createElement("img", "htbi-connection-image");
    image.src = new URL(connection.image, imageBaseUrl).href;
    image.alt = createConnectionAltText(connection, partsMetadata);
    image.width = 1080;
    image.height = 1080;
    if (!modal) image.loading = "lazy";
    const fallback = createElement("p", "htbi-connection-image-fallback", "Connection image unavailable");
    fallback.hidden = true;
    image.addEventListener("error", () => {
      image.hidden = true;
      fallback.hidden = false;
      console.warn(`Connection image could not be loaded: ${image.src}`);
    });
    wrapper.append(image, fallback);
    if (modal) return wrapper;
    const button = createElement("button", "htbi-connection-image-button");
    button.type = "button";
    button.setAttribute("aria-label", `View connection details. ${image.alt}`);
    button.addEventListener("click", onOpen);
    button.append(wrapper);
    return button;
  }
  function mountConnectionLibrary(root, {
    connections,
    partsIndex,
    partsMetadata,
    imageBaseUrl,
    random = Math.random,
    pageUrl = document.location.href,
    historyAdapter = window.history,
    locationAdapter = document.location,
    windowAdapter = window,
    navigatorAdapter = navigator
  }) {
    const searchForm = root.querySelector("[data-search-form]");
    const searchInput = root.querySelector("[data-search-input]");
    const suggestions = root.querySelector("[data-search-suggestions]");
    const clearQueryButton = root.querySelector("[data-clear-query]");
    const clearButton = root.querySelector("[data-clear-search]");
    const activeFilters = root.querySelector("[data-active-filters]");
    const filterChips = root.querySelector("[data-filter-chips]");
    const emptyClearButton = root.querySelector("[data-empty-clear]");
    const resultCount = root.querySelector("[data-result-count]");
    const grid = root.querySelector("[data-connection-grid]");
    const browseProgress = root.querySelector("[data-browse-progress]");
    const loadMoreButton = root.querySelector("[data-load-more]");
    const emptyState = root.querySelector("[data-empty-state]");
    const emptyTitle = root.querySelector("[data-empty-title]");
    const emptyMessage = root.querySelector("[data-empty-message]");
    const notice = root.querySelector("[data-library-notice]");
    const modal = root.querySelector("[data-connection-modal]");
    const modalContent = root.querySelector("[data-modal-content]");
    const modalClose = root.querySelector("[data-modal-close]");
    let selectedPartIds = [];
    let keywordQuery = "";
    let visibleSuggestions = [];
    let highlightedSuggestionIndex = -1;
    let modalOpener = null;
    let currentModalConnection = null;
    let modalHistoryMode = null;
    let sharePanel = null;
    let shareToggle = null;
    let visibleConnectionLimit = INITIAL_VISIBLE_CONNECTIONS;
    let matchingConnections = [];
    const consistency = validateLibraryConsistency(connections, partsIndex);
    if (!consistency.valid) {
      console.error("Parts Connection Library data consistency errors:", consistency.errors);
      notice.textContent = "Some library entries could not be displayed.";
      notice.hidden = false;
    }
    const metadataValidation = validatePartsMetadata(partsIndex, partsMetadata);
    if (!metadataValidation.valid) {
      console.error("Parts Connection Library metadata errors:", metadataValidation.errors);
    }
    if (metadataValidation.missingPartIds.length > 0) {
      console.warn(`Missing Part metadata: ${metadataValidation.missingPartIds.join(", ")}`);
    }
    const sessionConnections = shuffleConnections(connections, random);
    function closeSuggestions() {
      highlightedSuggestionIndex = -1;
      suggestions.hidden = true;
      searchInput.setAttribute("aria-expanded", "false");
      searchInput.removeAttribute("aria-activedescendant");
    }
    function updateSuggestionHighlight() {
      [...suggestions.children].forEach((option, index) => {
        const selected = index === highlightedSuggestionIndex;
        option.setAttribute("aria-selected", String(selected));
        option.classList.toggle("is-highlighted", selected);
      });
      if (highlightedSuggestionIndex < 0) {
        searchInput.removeAttribute("aria-activedescendant");
        return;
      }
      searchInput.setAttribute(
        "aria-activedescendant",
        suggestions.children[highlightedSuggestionIndex].id
      );
    }
    function selectSuggestion(partId) {
      addPartIds([partId]);
    }
    function refreshSuggestions() {
      visibleSuggestions = findMatchingParts(searchInput.value, partsMetadata, { limit: 8 });
      highlightedSuggestionIndex = -1;
      suggestions.replaceChildren();
      if (normalizePartSearchQuery(searchInput.value) === "" || visibleSuggestions.length === 0) {
        closeSuggestions();
        return;
      }
      visibleSuggestions.forEach((part, index) => {
        const option = createElement("li", "htbi-connection-search-suggestion");
        option.id = `htbi-connection-search-option-${index}`;
        option.setAttribute("role", "option");
        option.setAttribute("aria-selected", "false");
        option.append(
          createElement("span", "htbi-connection-search-suggestion-id", part.partId),
          createElement("span", "htbi-connection-search-suggestion-name", part.name)
        );
        option.addEventListener("pointerdown", (event) => event.preventDefault());
        option.addEventListener("click", () => selectSuggestion(part.partId));
        suggestions.append(option);
      });
      suggestions.hidden = false;
      searchInput.setAttribute("aria-expanded", "true");
    }
    function addPartIds(partIds) {
      const nextPartIds = canonicalizePartIds(
        [...selectedPartIds, ...partIds],
        partsIndex
      );
      searchInput.value = "";
      keywordQuery = "";
      closeSuggestions();
      selectedPartIds = nextPartIds;
      applyFilters();
      searchInput.focus();
    }
    function submitSearch() {
      const parsedPartIds = parsePartIdInput(searchInput.value);
      const knownPartIds = new Set(Object.keys(partsIndex).map(normalizePartId));
      if (parsedPartIds.length > 0 && parsedPartIds.every((partId) => knownPartIds.has(normalizePartId(partId)))) {
        addPartIds(parsedPartIds);
        return;
      }
      const exactMatch = findExactPartMatch(searchInput.value, partsMetadata);
      if (exactMatch) {
        addPartIds([exactMatch.partId]);
        return;
      }
      keywordQuery = searchInput.value;
      applyFilters();
      refreshSuggestions();
    }
    function removePartId(partId) {
      const normalizedPartId = normalizePartId(partId);
      selectedPartIds = selectedPartIds.filter(
        (selected) => normalizePartId(selected) !== normalizedPartId
      );
      applyFilters();
      searchInput.focus();
    }
    function clearFilters() {
      selectedPartIds = [];
      searchInput.value = "";
      keywordQuery = "";
      closeSuggestions();
      applyFilters();
      searchInput.focus();
    }
    function clearKeywordQuery() {
      searchInput.value = "";
      keywordQuery = "";
      closeSuggestions();
      applyFilters();
      searchInput.focus();
    }
    function renderFilterChips() {
      filterChips.replaceChildren();
      for (const partId of selectedPartIds) {
        const item = createElement("li", "htbi-connection-filter-chip");
        const label = createElement("span", "htbi-connection-filter-chip-label", partId);
        const remove = createElement("button", "htbi-connection-filter-chip-remove", "\xD7");
        remove.type = "button";
        remove.setAttribute("aria-label", `Remove Part ID ${partId}`);
        remove.addEventListener("click", () => removePartId(partId));
        item.append(label, remove);
        filterChips.append(item);
      }
      activeFilters.hidden = selectedPartIds.length === 0 && normalizePartSearchQuery(keywordQuery) === "";
    }
    function closeSharePanel() {
      if (!sharePanel || sharePanel.hidden) return false;
      sharePanel.hidden = true;
      shareToggle.setAttribute("aria-expanded", "false");
      shareToggle.focus();
      return true;
    }
    function closeModalView() {
      if (modal.open) modal.close();
    }
    function requestModalClose() {
      const historyMode = modalHistoryMode;
      closeModalView();
      if (historyMode === "pushed") {
        historyAdapter.back();
      } else if (historyMode === "direct") {
        historyAdapter.replaceState(null, "", removeConnectionDeepLink(locationAdapter.href));
      }
    }
    function legacyCopyText(value) {
      const input = document.createElement("textarea");
      input.value = value;
      input.setAttribute("readonly", "");
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.append(input);
      input.select();
      const copied = typeof document.execCommand === "function" && document.execCommand("copy");
      input.remove();
      return copied;
    }
    function createShareLink(label, href) {
      const link = createElement("a", "htbi-connection-share-option", label);
      link.href = href;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      return link;
    }
    function renderShareControls(connection) {
      const targets = buildConnectionShareTargets({
        pageUrl: locationAdapter.href || pageUrl,
        connection,
        partsMetadata,
        imageBaseUrl
      });
      const share = createElement("section", "htbi-connection-share");
      shareToggle = createElement("button", "htbi-connection-share-toggle", "Share");
      shareToggle.type = "button";
      shareToggle.setAttribute("aria-expanded", "false");
      shareToggle.setAttribute("aria-controls", "htbi-connection-share-panel");
      sharePanel = createElement("div", "htbi-connection-share-panel");
      sharePanel.id = "htbi-connection-share-panel";
      sharePanel.hidden = true;
      sharePanel.append(
        createShareLink("Facebook", targets.facebookUrl),
        createShareLink("Pinterest", targets.pinterestUrl),
        createShareLink("WhatsApp", targets.whatsappUrl),
        createShareLink("X", targets.xUrl)
      );
      const copyButton = createElement("button", "htbi-connection-share-option", "Copy Link");
      copyButton.type = "button";
      const status = createElement("p", "htbi-connection-share-status");
      status.setAttribute("role", "status");
      status.setAttribute("aria-live", "polite");
      copyButton.addEventListener("click", async () => {
        try {
          await copyTextWithFallback(targets.deepLinkUrl, {
            clipboard: navigatorAdapter.clipboard,
            fallback: legacyCopyText
          });
          status.textContent = "Link copied";
        } catch {
          status.textContent = "Copy unavailable. Select and copy the page URL.";
        }
      });
      sharePanel.append(copyButton);
      if (supportsNativeShare(navigatorAdapter)) {
        const nativeButton = createElement("button", "htbi-connection-share-option", "Share via device");
        nativeButton.type = "button";
        nativeButton.addEventListener("click", async () => {
          try {
            await navigatorAdapter.share({ text: targets.text, url: targets.deepLinkUrl });
          } catch (error) {
            if (error?.name !== "AbortError") {
              status.textContent = "Device sharing is unavailable. Copy the link instead.";
            }
          }
        });
        sharePanel.append(nativeButton);
      }
      shareToggle.addEventListener("click", () => {
        sharePanel.hidden = !sharePanel.hidden;
        shareToggle.setAttribute("aria-expanded", String(!sharePanel.hidden));
        if (!sharePanel.hidden) sharePanel.querySelector("a, button")?.focus();
      });
      share.append(shareToggle, sharePanel, status);
      return share;
    }
    function selectPartFromModal(partId) {
      modalOpener = null;
      requestModalClose();
      addPartIds([partId]);
    }
    function openModal(connection, opener, { historyMode = "push" } = {}) {
      if (modal.open && currentModalConnection?.id === connection.id) return;
      if (modal.open) closeModalView();
      modalOpener = opener;
      currentModalConnection = connection;
      modalHistoryMode = historyMode === "push" ? "pushed" : historyMode;
      modalContent.replaceChildren();
      const details = createElement("div", "htbi-connection-modal-details");
      details.append(
        renderParts(connection.baseParts, "base", selectPartFromModal, partsMetadata),
        renderParts(connection.connectionParts, "connection", selectPartFromModal, partsMetadata),
        renderShareControls(connection)
      );
      modalContent.append(renderImage(connection, imageBaseUrl, partsMetadata, { modal: true }), details);
      if (historyMode === "push") {
        historyAdapter.pushState(
          { htbiConnectionId: connection.id },
          "",
          buildConnectionDeepLink(locationAdapter.href || pageUrl, connection.id)
        );
      }
      modal.showModal();
      modalClose.focus();
    }
    function renderCard(connection) {
      const article = createElement("article", "htbi-connection-card");
      article.dataset.connectionId = connection.id;
      const imageButton = renderImage(connection, imageBaseUrl, partsMetadata, {
        onOpen: (event) => openModal(connection, event.currentTarget)
      });
      const parts = createElement("div", "htbi-connection-card-parts");
      parts.append(
        renderParts(connection.baseParts, "base", (partId) => addPartIds([partId]), partsMetadata),
        renderParts(connection.connectionParts, "connection", (partId) => addPartIds([partId]), partsMetadata)
      );
      article.append(imageButton, parts);
      return article;
    }
    function updateBrowseProgress() {
      const batch = createConnectionBatch(matchingConnections, visibleConnectionLimit);
      const matchingLabel = selectedPartIds.length > 0 || normalizePartSearchQuery(keywordQuery) !== "" ? " matching" : "";
      browseProgress.textContent = `Showing ${batch.visibleCount} of ${batch.totalCount}${matchingLabel} connections`;
      loadMoreButton.hidden = !batch.hasMore;
      return batch;
    }
    function applyFilters() {
      visibleConnectionLimit = INITIAL_VISIBLE_CONNECTIONS;
      const state = filterConnectionsBySearchState(
        sessionConnections,
        { exactPartIds: selectedPartIds, keywordQuery },
        partsIndex,
        partsMetadata
      );
      selectedPartIds = state.selectedPartIds;
      renderFilterChips();
      const displayQuery = String(keywordQuery).trim().replace(/\s+/g, " ");
      const hasKeyword = state.keywordQuery !== "";
      clearQueryButton.hidden = !hasKeyword;
      matchingConnections = state.connections;
      const batch = updateBrowseProgress();
      grid.replaceChildren(...batch.connections.map(renderCard));
      const countLabel = `${state.connections.length} ${state.connections.length === 1 ? "connection" : "connections"}`;
      resultCount.textContent = selectedPartIds.length === 0 && !hasKeyword ? countLabel : selectedPartIds.length === 0 ? `${countLabel} matching "${displayQuery}"` : selectedPartIds.length === 1 ? hasKeyword ? `${countLabel} using ${selectedPartIds[0]} and matching "${displayQuery}"` : `${countLabel} using ${selectedPartIds[0]}` : hasKeyword ? `${countLabel} using all ${selectedPartIds.length} selected parts and matching "${displayQuery}"` : `${countLabel} using all ${selectedPartIds.length} selected parts`;
      emptyState.hidden = state.connections.length > 0;
      grid.hidden = state.connections.length === 0;
      if (state.connections.length === 0) {
        emptyTitle.textContent = connections.length === 0 ? "No connections available" : "No connections found";
        emptyMessage.textContent = connections.length === 0 ? "The library does not contain any connection records yet." : hasKeyword && state.matchingParts.length === 0 ? `No parts match "${displayQuery}".` : "No connections match these filters. Remove a filter or clear all filters.";
      }
    }
    function loadMoreConnections() {
      const previousVisibleCount = grid.children.length;
      visibleConnectionLimit = getNextVisibleConnectionLimit(
        visibleConnectionLimit,
        matchingConnections.length
      );
      const batch = updateBrowseProgress();
      grid.append(...batch.connections.slice(previousVisibleCount).map(renderCard));
    }
    searchForm.addEventListener("submit", (event) => {
      event.preventDefault();
      submitSearch();
    });
    searchInput.addEventListener("input", () => {
      keywordQuery = searchInput.value;
      applyFilters();
      refreshSuggestions();
    });
    searchInput.addEventListener("focus", refreshSuggestions);
    searchInput.addEventListener("keydown", (event) => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        if (suggestions.hidden) refreshSuggestions();
        if (visibleSuggestions.length === 0) return;
        highlightedSuggestionIndex = event.key === "ArrowDown" ? (highlightedSuggestionIndex + 1) % visibleSuggestions.length : (highlightedSuggestionIndex - 1 + visibleSuggestions.length) % visibleSuggestions.length;
        updateSuggestionHighlight();
        return;
      }
      if (event.key === "Escape") {
        closeSuggestions();
        return;
      }
      if (event.key === "Tab") {
        closeSuggestions();
        return;
      }
      if (event.key !== "Enter") return;
      event.preventDefault();
      if (highlightedSuggestionIndex >= 0) {
        selectSuggestion(visibleSuggestions[highlightedSuggestionIndex].partId);
      } else {
        submitSearch();
      }
    });
    clearQueryButton.addEventListener("click", clearKeywordQuery);
    clearButton.addEventListener("click", clearFilters);
    emptyClearButton.addEventListener("click", clearFilters);
    loadMoreButton.addEventListener("click", loadMoreConnections);
    modalClose.addEventListener("click", requestModalClose);
    modal.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      if (!closeSharePanel()) requestModalClose();
    });
    modal.addEventListener("close", () => {
      modalContent.replaceChildren();
      modalOpener?.focus();
      modalOpener = null;
      currentModalConnection = null;
      modalHistoryMode = null;
      sharePanel = null;
      shareToggle = null;
    });
    windowAdapter.addEventListener("popstate", (event) => {
      const connection = resolveDeepLinkedConnection(locationAdapter.href, connections);
      if (connection) {
        openModal(connection, null, {
          historyMode: event.state?.htbiConnectionId === connection.id ? "pushed" : "direct"
        });
      } else {
        closeModalView();
      }
    });
    applyFilters();
    const deepLinkedConnection = resolveDeepLinkedConnection(locationAdapter.href || pageUrl, connections);
    if (deepLinkedConnection) openModal(deepLinkedConnection, null, { historyMode: "direct" });
    return {
      addPartIds,
      clearFilters,
      loadMoreConnections,
      removePartId,
      get visibleConnectionCount() {
        return grid.children.length;
      },
      get selectedPartIds() {
        return [...selectedPartIds];
      },
      get sessionConnections() {
        return [...sessionConnections];
      }
    };
  }

  // src/ui/main.js
  async function loadJson(url, label) {
    const response = await fetch(url, { credentials: "same-origin" });
    if (!response.ok) throw new Error(`${label} request failed with status ${response.status}`);
    return response.json();
  }
  async function start(root) {
    const notice = root.querySelector("[data-library-notice]");
    const count = root.querySelector("[data-result-count]");
    try {
      const connectionsUrl = new URL(root.dataset.connectionsUrl, document.baseURI);
      const partsIndexUrl = new URL(root.dataset.partsIndexUrl, document.baseURI);
      const partsMetadataUrl = new URL(root.dataset.partsMetadataUrl, document.baseURI);
      const imageBaseUrl = new URL(root.dataset.imageBaseUrl, document.baseURI);
      const [connections, partsIndex, partsMetadata] = await Promise.all([
        loadJson(connectionsUrl, "Connection data"),
        loadJson(partsIndexUrl, "Parts index"),
        loadJson(partsMetadataUrl, "Part metadata")
      ]);
      mountConnectionLibrary(root, { connections, partsIndex, partsMetadata, imageBaseUrl });
    } catch (error) {
      console.error("Parts Connection Library failed to start:", error);
      count.textContent = "0 connections";
      notice.textContent = "The connection library could not be loaded. Please refresh and try again.";
      notice.hidden = false;
    }
  }
  document.querySelectorAll("[data-htbi-connection-library]").forEach(start);
})();
